import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import {
  startWith,
  debounceTime,
  distinctUntilChanged,
  map,
} from 'rxjs/operators';

type UserOrTeam = 'user' | 'team';
@Component({
  selector: 'zint-permissions',
  templateUrl: './zint-permissions.component.html',
  styleUrl: './zint-permissions.component.css',
  standalone: false,
})
export class ZintPermissionsComponent {
  @Input() allUsers: Record<string, any>[] = [];
  @Input() allTeams: Record<string, any>[] = [];
  @Input() existingTeams: Record<string, any>[] = [];
  @Input() existingUsers: Record<string, any>[] = [];
  @Input() usersLabel = 'Individuals';
  @Input() teamsLabel = 'Teams';
  @Input() permissionLabel = '';

  // NEW: Track selected IDs locally instead of modifying the user objects. This is for speed.
  selectedUserIds = new Set<number>();
  selectedTeamIds = new Set<number>();

  @Output() onPermissionChange = new EventEmitter();

  // search inputs
  userSearchControl = new FormControl('');
  teamSearchControl = new FormControl('');

  // Observables for filtered lists
  filteredUsers$: Observable<Record<string, any>[]>;
  filteredTeams$: Observable<Record<string, any>[]>;

  // state for dropdown visibility
  userFieldFocused: boolean = false;
  teamFieldFocused: boolean = false;

  // selected items lists
  selectedUsers: Record<string, any>[] = [];
  selectedTeams: Record<string, any>[] = [];

  ngOnInit(): void {
    // Setup user search observable
    this.filteredUsers$ = this.userSearchControl.valueChanges.pipe(
      startWith(this.userSearchControl.value || ''),
      debounceTime(220),
      distinctUntilChanged(),
      map(searchTerm =>
        this.filterUsersOrTeams(searchTerm.trim() || '', 'user')
      )
    );

    // Setup team search observable
    this.filteredTeams$ = this.teamSearchControl.valueChanges.pipe(
      startWith(this.teamSearchControl.value || ''),
      debounceTime(220),
      distinctUntilChanged(),
      map(searchTerm =>
        this.filterUsersOrTeams(searchTerm.trim() || '', 'team')
      )
    );

    // show existing selections
    this.handleExistingPermissions();
    this.updateSelectedUsersOrTeamsList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['existingUsers'] || changes['existingTeams']) {
      this.handleExistingPermissions();
      this.updateSelectedUsersOrTeamsList();
      return;
    }
    if (changes['allUsers']) {
      this.updateSelectedUsersOrTeamsList('user');
    }
    if (changes['allTeams']) {
      this.updateSelectedUsersOrTeamsList('team');
    }
  }

  filterUsersOrTeams(
    searchTerm: string,
    group: UserOrTeam
  ): Record<string, any>[] {
    const lowerCaseSearchTerm = searchTerm.toLowerCase();

    if (group === 'user') {
      return this.allUsers?.filter(user =>
        user.email.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }
    if (group === 'team') {
      return this.allTeams?.filter(team =>
        team.name.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }
  }

onSelectionChange({ userOrTeamObj, isChecked, group }: { userOrTeamObj: Record<string, any>; isChecked: boolean; group: UserOrTeam; }): void {
  if (group === 'user') {
    if (isChecked) {
      this.selectedUserIds.add(userOrTeamObj.userId);
    } else {
      this.selectedUserIds.delete(userOrTeamObj.userId);
    }
    // Update the visual list of selected items for the badges
    this.updateSelectedUsersOrTeamsList('user');
    // Emit the original object (unchanged)
    this.onPermissionChange.emit({ ...userOrTeamObj, selected: isChecked });
  }
  else if (group === 'team') {
    if (isChecked) {
      this.selectedTeamIds.add(userOrTeamObj.id);
    } else {
      this.selectedTeamIds.delete(userOrTeamObj.id);
    }
    this.updateSelectedUsersOrTeamsList('team');
    this.onPermissionChange.emit({ ...userOrTeamObj, selected: isChecked });
  }
}

  removeSelectedUserOrTeam(
    objToRemove: Record<string, any>,
    group: UserOrTeam
  ): void {
    let foundObj = null;

    if (group === 'user') {
      foundObj = this.allUsers.find(u => u.userId === objToRemove.userId);
    } else if (group === 'team') {
      foundObj = this.allTeams.find(t => t.id === objToRemove.id);
    }

    if (foundObj) {
      foundObj.selected = false;
      this.updateSelectedUsersOrTeamsList(group);

      this.onPermissionChange.emit(foundObj);
    }
  }

  handleExistingPermissions(): void {
    // Reset sets
    this.selectedUserIds.clear();
    this.selectedTeamIds.clear();

    // Populate from existing inputs (passed from parent)
    this.existingUsers?.forEach(u => this.selectedUserIds.add(u.userId));
    this.existingTeams?.forEach(t => this.selectedTeamIds.add(t.id));
    // No need to map/clone 'allUsers' or 'allTeams'.
    // We use the shared reference directly.
  }

  existingPermissionShouldBeSelected({
    group,
    searchKey,
  }: {
    group: UserOrTeam;
    searchKey?: string;
  }): boolean {
    let foundExistingObj: Record<string, any> = null;

    foundExistingObj =
      group === 'team'
        ? this.existingTeams.find(team => team.name === searchKey)
        : this.existingUsers.find(user => user.email === searchKey);

    return !!foundExistingObj;
  }

  updateSelectedUsersOrTeamsList(group?: UserOrTeam): void {
    if (!group || group === 'user') {
      // Filter the shared list against our local Set
      this.selectedUsers = this.allUsers?.filter(user => this.selectedUserIds.has(user.userId));
    }

    if (!group || group === 'team') {
      this.selectedTeams = this.allTeams?.filter(team => this.selectedTeamIds.has(team.id));
    }
  }


}
